import {
    Button,
    Checkbox,
    Spinner
} from "@fluentui/react-components";
import { DataGrid, DataGridBody, DataGridCell, DataGridHeader, DataGridHeaderCell, DataGridRow, TableColumnDefinition, createTableColumn } from "@fluentui/react-table";
import { AddRegular, EditRegular, DeleteRegular, GlobeLocationRegular, SelectObjectSkewRegular} from "@fluentui/react-icons";
import Paginator from "#root/components/Paginator";
import { useLocations, useUpdateLocation, useDeleteLocation, useCreateLocation, Location } from "#root/queries/locations";
import { useState } from "react";
import "./styles.scss";
import LoadingWrapper from "#root/components/LoadingWrapper";
import { showToast } from "#root/components/GlobalToaster";
import { DialogBox } from "#root/components/DialogBox";
import { Breadcrumbs } from "#root/components/Breadcrumbs";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "#root/routes/constants";
import { useIncidentById } from "#root/queries/incidents";
import React from "react";
import LocationDialog from "./LocationDialog";

const IncidentLocations: React.FC = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [selectedLocations, setSelectedLocations] = useState<Set<string>>(new Set());
    const [editingLocation, setEditingLocation] = useState<Location | null>(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [locationsToDelete, setLocationsToDelete] = useState<Location[]>([]);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    
    const incidentId = searchParams.get('incidentId');

    if (!incidentId) {
        navigate(ROUTES.ERROR_404);
    }

    const { data: incidentData, isLoading: incidentLoading } = useIncidentById({ id: incidentId! });
    const incident = incidentData?.item;

    const {
        data: locationsData,
        isLoading,
        refetch
    } = useLocations({
        take: itemsPerPage,
        skip: (currentPage - 1) * itemsPerPage,
        incidentId: incidentId!,
        includeOrganizationLocations: false
    });

    const updateLocationMutation = useUpdateLocation();
    const deleteLocationMutation = useDeleteLocation();
    const createLocationMutation = useCreateLocation();

    const totalCount = locationsData?.totalCount ?? 0;
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    const locations = locationsData?.items ?? [];

    // Pagination handling
    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
        setSelectedLocations(new Set());
    };

    const handleItemsPerPageChange = (newItemsPerPage: number): void => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
    };

    // Selection handling
    const onSelectionChange = (event: React.MouseEvent | React.KeyboardEvent, data: any) => {
        // Only allow selection via checkbox click
        const target = event.target as HTMLElement;
        if (target.closest('[type="checkbox"]')) {
            setSelectedLocations(new Set(data.selectedItems));
        }
    };

    const getSelectionState = () => {
        if (selectedLocations.size === 0) return false;
        if (selectedLocations.size === locations.length) return true;
        return 'mixed';
    };

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            const newSelection = new Set<string>();
            locations.forEach(location => {
                if (location.id) {
                    newSelection.add(location.id);
                }
            });
            setSelectedLocations(newSelection);
        } else {
            setSelectedLocations(new Set());
        }
    };

    // Edit groups handling
    const handleEditGroups = (location: Location) => {
        // TODO: navigate to grops page
    };

    // Edit handling
    const handleEdit = (location: Location) => {
        setEditingLocation(location);
    };

    const handleEditConfirm = async (updatedLocation: Location) => {
        if (editingLocation?.id) {
            try {
                await updateLocationMutation.mutateAsync({
                    location:
                    {
                        id: editingLocation.id,
                        ...updatedLocation
                    }
                });
                showToast('Location updated successfully', 'success');
                setEditingLocation(null);
                refetch();
            } catch (error) {
                showToast('Failed to update location', 'error');
            }
        }
    };

    // Delete handling
    const handleDeleteClick = (location: Location, e?: React.MouseEvent) => {
        e?.stopPropagation();
        setLocationsToDelete([location]);
        setIsDeleteDialogOpen(true);
    };

    const handleDeleteMultipleClick = () => {
        const toDelete = locations.filter(loc => loc.id && selectedLocations.has(loc.id));
        setLocationsToDelete(toDelete);
        setIsDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        try {
            await Promise.all(locationsToDelete.map(location => {
                return deleteLocationMutation.mutateAsync({
                    id: location.id!
                });
            }));
            setIsDeleteDialogOpen(false);
            showToast(`${locationsToDelete.length > 1 ? 'Locations' : 'Location'} deleted successfully`, 'success');
            setSelectedLocations(new Set());
            setLocationsToDelete([]);
            refetch();
        } catch (error) {
            showToast('Failed to delete location(s)', 'error');
        }
    };

    // Create handling
    const handleConfirmCreate = async (newLocationData: Location) => {
        try {
            await createLocationMutation.mutateAsync({
                location: {
                    ...newLocationData,
                    incidentId: incidentId!
                }
            });
            setIsCreateDialogOpen(false);
            showToast(`Location created successfully`, 'success');
            refetch();
        } catch (error) {
            showToast(`Failed to create location`, 'error');
        }
    };

    // Define DataGrid columns
    const columns: TableColumnDefinition<Location>[] = [
        createTableColumn<Location>({
            columnId: "name",
            renderHeaderCell: () => "Location",
            renderCell: (item) => {
                return <div title={item.name}>{item.name}</div>;
            },
        }),
        createTableColumn<Location>({
            columnId: "address",
            renderHeaderCell: () => "Address",
            renderCell: (item) => {
                return <div title={item.address}>{item.address}</div>;
            },
        }),
        createTableColumn<Location>({
            columnId: "actions",
            renderHeaderCell: () => "Actions",
            renderCell: (item) => {
                return (
                    <>
                        <Button
                            title="Edit Location"
                            appearance="subtle"
                            icon={<EditRegular />}
                            onClick={() => handleEdit(item)}
                            disabled={updateLocationMutation.isPending}
                        />
                        <Button
                            title="Edit Groups"
                            appearance="subtle"
                            icon={<SelectObjectSkewRegular />}
                            onClick={() => handleEditGroups(item)}
                        />
                        <Button
                            title="Delete Location"
                            appearance="subtle"
                            icon={<DeleteRegular />}
                            onClick={(e) => handleDeleteClick(item, e)}
                            disabled={deleteLocationMutation.isPending}
                        />
                    </>
                );
            },
        }),
    ];

    if (incidentLoading) {
        return <Spinner size="large" />;
    }

    return (
        <div className="incident-locations-page">
            <div className="emc-page-header">
                <div className="emc-page-header__top-row">
                    <div className="emc-page-header__breadcrumbs">
                        <Breadcrumbs currentPageName={`${incident?.name ? `${incident.name} - ` : ''}Locations`}/>
                    </div>
                </div>
                <div className="emc-page-header__title">
                    <h2>
                        Locations 
                        <div className="divider" />
                        <Button
                            appearance="primary"
                            icon={<AddRegular />}
                            onClick={() => setIsCreateDialogOpen(true)}
                        >
                            New Location
                        </Button>
                    </h2>
                </div>
            </div>

            <LoadingWrapper
                isLoading={isLoading}
                isEmpty={locations.length === 0}
                emptyMessage={`No locations found.`}
                emptyIcon={<GlobeLocationRegular />}
            >
                <div className="emc-grid">
                    <DataGrid
                        className="emc-datagrid"
                        items={locations}
                        columns={columns}
                        selectionMode="multiselect"
                        selectedItems={selectedLocations}
                        onSelectionChange={onSelectionChange}
                        getRowId={(item) => item.id!}
                        focusMode="none"
                    >
                        <DataGridHeader>
                            <DataGridRow>
                                {({ renderHeaderCell, columnId }) => (
                                    <DataGridHeaderCell className={columnId.toString()}>
                                        {renderHeaderCell()}
                                    </DataGridHeaderCell>
                                )}
                            </DataGridRow>
                        </DataGridHeader>
                        <DataGridBody<Location>>
                            {({ item, rowId }) => (
                                <DataGridRow<Location>
                                    key={rowId}
                                    selectionCell={{ "aria-label": "Select row" }}
                                    className={editingLocation?.id === item.id ? 'editing' : ''}
                                >
                                    {({ renderCell, columnId }) => (
                                        <DataGridCell className={columnId.toString()}>
                                            {renderCell(item)}
                                        </DataGridCell>
                                    )}
                                </DataGridRow>
                            )}
                        </DataGridBody>
                    </DataGrid>

                    <Paginator
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        itemsPerPage={itemsPerPage}
                        onItemsPerPageChange={handleItemsPerPageChange}
                        totalItems={totalCount}
                        currentItems={(currentPage - 1) * itemsPerPage + 1}
                        showItemsPerPage={true}
                    />
                </div>
            </LoadingWrapper>

            {selectedLocations.size > 0 && (
                <div className="emc-page-footer">
                    <Checkbox
                        checked={getSelectionState()}
                        onChange={(_, data) => handleSelectAll(!!data.checked)}
                        label="Select All"
                    />
                    <Button
                        appearance="secondary"
                        onClick={() => setSelectedLocations(new Set())}
                    >
                        Cancel
                    </Button>
                    <Button
                        appearance="primary"
                        icon={<DeleteRegular />}
                        onClick={handleDeleteMultipleClick}
                    >
                        Delete Selected
                    </Button>
                </div>
            )}

            <DialogBox
                open={isDeleteDialogOpen}
                onOpenChange={setIsDeleteDialogOpen}
                title={`Delete ${locationsToDelete.length > 1 ? 'Locations' : 'Location'}`}
                primaryButtonText="Delete"
                onPrimaryButtonClick={() => handleConfirmDelete()}
                isPrimaryButtonLoading={deleteLocationMutation.isPending}
            >
                Permanently delete {locationsToDelete.length === 1 ? `location ${locationsToDelete[0]?.name}` : `${locationsToDelete.length} locations`}?
                You can't undo this action.
            </DialogBox>

            <LocationDialog
                isOpen={isCreateDialogOpen}
                onClose={() => setIsCreateDialogOpen(false)}
                onSave={handleConfirmCreate}
                isLoading={createLocationMutation.isPending}
            />

            {editingLocation && (
                <LocationDialog
                    isOpen={!!editingLocation}
                    onClose={() => setEditingLocation(null)}
                    onSave={handleEditConfirm}
                    isLoading={updateLocationMutation.isPending}
                    initialLocation={editingLocation}
                />
            )}
        </div>
    );
};

export default IncidentLocations;
